import { useState } from 'react';
import spotifyService from '../services/spotifyService';
import deezerService from '../services/deezerService';
import authService from '../services/authService';

export default function usePlaylistSearch() {
  const [playlists, setPlaylists] = useState({ spotify: [], deezer: [] });
  const [loading, setLoading] = useState({ spotify: false, deezer: false });
  const [error, setError] = useState({ spotify: null, deezer: null });
  const [offset, setOffset] = useState({ spotify: 0, deezer: 0 });
  const [total, setTotal] = useState({ spotify: 0, deezer: 0 });
  const [hasMore, setHasMore] = useState({ spotify: true, deezer: true });

  const search = async (searchTerm, platform = 'all') => {
    if (!searchTerm.trim()) return;

    // Réinitialisation des états pour la/les plateforme(s) concernée(s)
    const resetPlatform = (p) => {
      setLoading(prev => ({ ...prev, [p]: true }));
      setError(prev => ({ ...prev, [p]: null }));
      setOffset(prev => ({ ...prev, [p]: 0 }));
      setPlaylists(prev => ({ ...prev, [p]: [] }));
      setHasMore(prev => ({ ...prev, [p]: true }));
    };

    if (platform === 'all' || platform === 'spotify') {
      resetPlatform('spotify');
      try {
        if (authService.isAuthenticated()) {
          const response = await spotifyService.searchPlaylists(searchTerm, 50, 0);
          if (response?.playlists) {
            const newPlaylists = response.playlists.items
              .filter(item => item !== null)
              .map(item => ({ ...item, platform: 'spotify' }));
            
            setPlaylists(prev => ({ ...prev, spotify: newPlaylists }));
            setTotal(prev => ({ ...prev, spotify: response.playlists.total }));
            setOffset(prev => ({ ...prev, spotify: 50 }));
            setHasMore(prev => ({ 
              ...prev, 
              spotify: 50 < response.playlists.total 
            }));
          }
        }
      } catch (err) {
        console.error('Erreur Spotify:', err);
        setError(prev => ({ ...prev, spotify: err.message }));
        if (err.message.includes('No access token available')) {
          window.location.href = authService.getLoginUrl();
        }
      } finally {
        setLoading(prev => ({ ...prev, spotify: false }));
      }
    }

    if (platform === 'all' || platform === 'deezer') {
      resetPlatform('deezer');
      try {
        const response = await deezerService.searchPlaylists(searchTerm, 50, 0);
        if (response?.playlists) {
          const newPlaylists = response.playlists.items
            .filter(item => item !== null)
            .map(item => ({ ...item, platform: 'deezer' }));
          
          setPlaylists(prev => ({ ...prev, deezer: newPlaylists }));
          setTotal(prev => ({ ...prev, deezer: response.playlists.total }));
          setOffset(prev => ({ ...prev, deezer: 50 }));
          setHasMore(prev => ({ 
            ...prev, 
            deezer: 50 < response.playlists.total 
          }));
        }
      } catch (err) {
        console.error('Erreur Deezer:', err);
        setError(prev => ({ ...prev, deezer: err.message }));
      } finally {
        setLoading(prev => ({ ...prev, deezer: false }));
      }
    }
  };

  const loadMore = async (platform) => {
    setLoading(prev => ({ ...prev, [platform]: true }));
    setError(prev => ({ ...prev, [platform]: null }));

    try {
      const service = platform === 'spotify' ? spotifyService : deezerService;
      const currentOffset = offset[platform];

      const response = await service.searchPlaylists('', 50, currentOffset);
      
      if (response?.playlists) {
        const newPlaylists = response.playlists.items
          .filter(item => item !== null)
          .map(item => ({ ...item, platform }));
        
        setPlaylists(prev => ({
          ...prev,
          [platform]: [...prev[platform], ...newPlaylists]
        }));
        
        setTotal(prev => ({
          ...prev,
          [platform]: response.playlists.total
        }));
        
        setOffset(prev => ({
          ...prev,
          [platform]: currentOffset + 50
        }));
        
        setHasMore(prev => ({
          ...prev,
          [platform]: currentOffset + 50 < response.playlists.total
        }));
      }
    } catch (err) {
      console.error(`Erreur ${platform}:`, err);
      setError(prev => ({ ...prev, [platform]: err.message }));
      if (platform === 'spotify' && err.message.includes('No access token available')) {
        window.location.href = authService.getLoginUrl();
      }
    } finally {
      setLoading(prev => ({ ...prev, [platform]: false }));
    }
  };

  return {
    playlists,
    loading,
    error,
    hasMore,
    total,
    search,
    loadMore
  };
}
